import { Link } from "react-router-dom";
import {
    Mail,
    Camera,
    CreditCard,
    Check,
    AlertTriangle,
    Shield,
    Archive,
    ShoppingBag,
    Store,
    Coffee,
    ArrowRight,
    ShieldCheck,
    Building2,
    Sparkles,
} from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import ThreeWaysDiagram from "@/components/ThreeWaysDiagram";


// --------- 3-paths converging diagram (the heart of the page) ----
// --------- Concrete case studies ---------------------------------
const CASES = [
    {
        icon: ShoppingBag,
        place: "Amazon Business",
        amount: "47,90 €",
        flow: "auto",
        steps: [
            "Vous achetez en ligne, paiement carte",
            "Amazon envoie l'e-reçu sur votre mail pro",
            "Ticketify scanne Gmail, extrait le PDF",
            "Match auto à la transaction CB Amazon",
        ],
        result: "Dans le Coffre en < 30 sec",
        accent: "#007AFF",
    },
    {
        icon: Store,
        place: "Carrefour Market",
        amount: "63,42 €",
        flow: "auto",
        steps: [
            "Vous présentez votre carte fidélité au TPE",
            "Vous payez normalement",
            "Carrefour vous envoie l'e-ticket par mail",
            "Ticketify le récupère et le matche",
        ],
        result: "Activez 'E-ticket' dans l'app Carrefour, une fois",
        accent: "#34C759",
    },
    {
        icon: Coffee,
        place: "Boulangerie du coin",
        amount: "4,80 €",
        flow: "photo",
        steps: [
            "Vous payez au TPE, vous recevez le ticket papier",
            "Onglet Transactions : la ligne 'Boulangerie' apparaît",
            "Tap 'Ajouter photo' → caméra",
            "GPT-5.2 extrait articles + TVA + SIRET",
        ],
        result: "7 secondes. Vous pouvez jeter le papier.",
        accent: "#FF9F0A",
    },
];

function CaseCard({ c, i }) {
    const isAuto = c.flow === "auto";
    return (
        <div
            className="tk-card p-7 tk-rise"
            style={{ animationDelay: `${i * 120}ms` }}
            data-testid={`case-${c.flow}-${i}`}
        >
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <div
                        className="w-11 h-11 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: `${c.accent}18` }}
                    >
                        <c.icon className="w-5 h-5" style={{ color: c.accent }} />
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">
                            Cas concret
                        </p>
                        <h3 className="text-lg font-semibold text-[#1D1D1F]">{c.place}</h3>
                    </div>
                </div>
                <p className="text-lg font-semibold tabular-nums text-[#1D1D1F]">{c.amount}</p>
            </div>

            <ol className="space-y-2.5">
                {c.steps.map((s, idx) => (
                    <li key={`${c.title}-step-${idx}`} className="flex items-start gap-3 text-sm">
                        <span
                            className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold tabular-nums"
                            style={{
                                backgroundColor: `${c.accent}18`,
                                color: c.accent,
                            }}
                        >
                            {idx + 1}
                        </span>
                        <span className="text-[#1D1D1F]/90 leading-snug">{s}</span>
                    </li>
                ))}
            </ol>

            <div
                className="mt-6 pt-4 border-t border-black/5 flex items-center gap-2 text-sm font-medium"
                style={{ color: isAuto ? "#34C759" : "#FF9F0A" }}
            >
                {isAuto ? <Check className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
                {c.result}
            </div>
        </div>
    );
}


// --------- Merchant catalogue ------------------------------------
const MERCHANT_CATEGORIES = [
    {
        title: "E-commerce",
        merchants: ["Amazon", "Cdiscount", "Fnac.com", "Darty.com", "Backmarket", "ManoMano", "Apple Store online"],
    },
    {
        title: "Transport",
        merchants: ["SNCF Connect", "Trainline", "Air France", "EasyJet", "Uber", "Bolt", "FreeNow", "BlaBlaCar", "FlixBus", "Eurostar"],
    },
    {
        title: "SaaS & Tech",
        merchants: ["Apple", "Google Workspace", "Microsoft 365", "Adobe", "Notion", "Slack", "GitHub", "Figma", "Stripe", "Spotify", "Netflix"],
    },
    {
        title: "Cloud & Infra",
        merchants: ["OVH Cloud", "Scaleway", "AWS", "Google Cloud", "Cloudflare", "Hetzner", "Vercel"],
    },
    {
        title: "Hôtellerie & Voyage",
        merchants: ["Booking.com", "Airbnb", "Hotels.com", "Hilton", "Marriott", "Accor", "GetYourGuide"],
    },
    {
        title: "Restauration connectée",
        merchants: ["Sunday", "Yavin", "TheFork", "Deliveroo", "Uber Eats", "Just Eat", "Frichti"],
    },
    {
        title: "Énergie & Péages",
        merchants: ["Total Energies", "Shell", "BP", "Liber-t", "Vinci Autoroutes", "APRR", "Fulli", "Bip&Go"],
    },
    {
        title: "Drive & Livraison",
        merchants: ["Carrefour Drive", "Auchan Drive", "Leclerc Drive", "Monoprix.fr", "Picard", "Decathlon.fr"],
    },
    {
        title: "Avec carte fidélité",
        merchants: ["Carrefour", "Monoprix", "Decathlon", "Fnac+", "Leclerc", "Sephora", "IKEA Family", "Apple Store physique"],
        muted: true,
    },
];


export default function HowItWorks() {
    return (
        <div className="min-h-screen">
            <PublicHeader />

            {/* Hero */}
            <section className="pt-32 pb-12 sm:pt-44 sm:pb-16 px-6 sm:px-8 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3 tk-rise">
                    Comment ça marche
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1D1D1F] tk-rise tk-rise-1 leading-[1.05]">
                    Trois voies.
                    <br />
                    <span className="bg-gradient-to-r from-[#007AFF] to-[#34C759] bg-clip-text text-transparent">
                        Un seul coffre.
                    </span>
                </h1>
                <p className="mt-6 max-w-2xl mx-auto text-lg text-[#86868B] leading-relaxed tk-rise tk-rise-2">
                    Pas de magie. Trois façons d&apos;attraper vos tickets, selon la situation. Toutes mènent au même endroit&nbsp;:
                    votre Coffre, prêt pour la compta.
                </p>
            </section>

            {/* 3-paths diagram */}
            <section className="px-6 sm:px-8 pb-24" data-testid="three-ways-section">
                <div className="max-w-6xl mx-auto">
                    <ThreeWaysDiagram />
                </div>
            </section>

            {/* Concrete cases */}
            <section className="px-6 sm:px-8 py-20 sm:py-24" data-testid="concrete-cases">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">
                            En pratique
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                            Trois cas, trois minutes, et c&apos;est compris.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {CASES.map((c, i) => (
                            <CaseCard key={c.title} c={c} i={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* The "you don't search anymore" insight */}
            <section className="px-6 sm:px-8 py-20">
                <div className="max-w-4xl mx-auto tk-card p-8 sm:p-12 tk-cta-dark bg-[#1D1D1F] text-white">
                    <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-center">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#34C759] flex items-center justify-center shrink-0">
                            <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2">
                                Le déclic
                            </p>
                            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                                Vous ne cherchez plus le ticket.<br />
                                <span className="text-white/70">C&apos;est le ticket qui cherche sa transaction.</span>
                            </h2>
                            <p className="mt-4 text-base text-white/75 leading-relaxed">
                                L&apos;app vous tape sur l&apos;épaule&nbsp;: <em>«&nbsp;Boulangerie Saint-Honoré, 4,80&nbsp;€ ce matin, t&apos;as toujours le ticket&nbsp;?&nbsp;»</em>. Vous tap, vous shoot. C&apos;est fini.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Merchants catalogue */}
            <section className="px-6 sm:px-8 py-20 sm:py-28" data-testid="merchants-catalogue">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">
                            Catalogue
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                            Reconnus automatiquement.
                        </h2>
                        <p className="mt-4 text-base text-[#86868B] max-w-2xl mx-auto">
                            Non exhaustif. La liste s&apos;étoffe chaque semaine au fil des nouveaux marchands repérés.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {MERCHANT_CATEGORIES.map((cat, i) => (
                            <div
                                key={cat.title}
                                className="tk-card p-6 tk-rise"
                                style={{ animationDelay: `${i * 80}ms` }}
                                data-testid={`cat-${cat.title.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-sm font-bold uppercase tracking-wider text-[#1D1D1F]">
                                        {cat.title}
                                    </h3>
                                    {cat.muted ? (
                                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FF9F0A]/15 text-[#FF9F0A]">
                                            Opt-in
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#34C759]/15 text-[#34C759]">
                                            Auto
                                        </span>
                                    )}
                                </div>
                                <ul className="space-y-1.5">
                                    {cat.merchants.map((m) => (
                                        <li
                                            key={m}
                                            className="text-sm text-[#1D1D1F]/90 flex items-center gap-2"
                                        >
                                            <span className="w-1 h-1 rounded-full bg-[#AEAEB2]" />
                                            {m}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 text-center text-xs text-[#86868B] max-w-xl mx-auto">
                        <strong>Opt-in</strong>&nbsp;: activez « e-ticket par mail » sur l&apos;app du marchand (1 fois, ~2 min).
                        Ensuite, chaque passage en caisse est récupéré auto.
                    </p>
                </div>
            </section>

            {/* What we CANNOT auto */}
            <section className="px-6 sm:px-8 py-20" data-testid="manual-section">
                <div className="max-w-3xl mx-auto tk-card p-8 sm:p-10">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-11 h-11 rounded-2xl bg-[#FF9F0A]/15 flex items-center justify-center">
                            <Camera className="w-5 h-5 text-[#FF9F0A]" />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF9F0A]">
                                Voie 3 · Photo manuelle
                            </p>
                            <h3 className="text-lg font-semibold text-[#1D1D1F]">Ce qui passe toujours par la photo</h3>
                        </div>
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-2.5 text-sm text-[#1D1D1F]/90">
                        {[
                            "Boulangerie / café / bistrot indépendant",
                            "Pharmacie de quartier",
                            "Tabac / presse",
                            "Petit commerce de quartier",
                            "Garage indépendant / pressing",
                            "Carrefour/Auchan/Lidl sans opt-in",
                            "Paiement en espèces",
                            "Marchés, food trucks",
                        ].map((t) => (
                            <li key={t} className="flex items-start gap-2">
                                <Camera className="w-3.5 h-3.5 text-[#FF9F0A] mt-0.5 shrink-0" />
                                {t}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-6 text-sm text-[#86868B] leading-relaxed">
                        Pour ces cas, l&apos;onglet <strong className="text-[#1D1D1F]">Transactions</strong> affiche la
                        ligne bancaire avec un badge orange « Photo ». Un tap, l&apos;IA fait le reste. <strong className="text-[#1D1D1F]">7 secondes max.</strong>
                    </p>
                </div>
            </section>

            {/* Why no bank API */}
            <section className="px-6 sm:px-8 py-20">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-10">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B] mb-3">
                            Honnêteté technique
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                            Pourquoi aucune API bancaire ne donne le détail.
                        </h2>
                    </div>
                    <div className="tk-card p-7 sm:p-8 space-y-4 text-[#1D1D1F]/90 leading-relaxed text-[15px]">
                        <p>
                            Quand vous insérez votre carte dans un TPE, le terminal n&apos;envoie au réseau Visa/Mastercard
                            que <strong>4 informations</strong>&nbsp;: montant, marchand, date, 4 derniers chiffres de la carte.
                        </p>
                        <p>
                            <strong>Aucun article. Aucune TVA. Aucun n° de facture.</strong> C&apos;est une limite physique du protocole.
                            Revolut, Qonto, Bridge, Tink, Plaid&nbsp;: tous ont accès aux mêmes 4 infos. Pas une de plus.
                        </p>
                        <p>
                            C&apos;est pour ça que notre approche est différente&nbsp;: on attrape le détail là où il existe vraiment
                            (mail des marchands) et on automatise la dernière étape (photo) là où il n&apos;existe que sur papier.
                        </p>
                    </div>
                    <p className="mt-6 text-center text-sm">
                        <Link to="/honnetete" className="text-[#007AFF] font-semibold hover:underline">
                            Voir la page Honnêteté complète →
                        </Link>
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 sm:px-8 py-24">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                        Tout ça pendant que vous travaillez ailleurs.
                    </h2>
                    <p className="mt-3 text-[#86868B]">5 minutes pour tout brancher. Le reste se passe tout seul.</p>
                    <Link
                        to="/register"
                        className="tk-btn tk-btn-primary mt-7 inline-flex tk-cta-glow"
                        data-testid="hiw-cta"
                    >
                        Créer mon compte <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <footer className="border-t border-black/5 py-10 px-6 sm:px-8 text-center text-sm text-[#86868B]">
                © 2026 Ticketify
            </footer>
        </div>
    );
}
